import React from 'react'
import {
  useFloating,
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
  useInteractions,
  useDismiss,
  useRole,
} from '@floating-ui/react'
import { CSSTransition } from 'react-transition-group'
import classNames from 'classnames'
import SVGIcon from '@components/SVGIcon'
import Button from '@components/Button'

interface FloatingModalProps {
  show: boolean
  onClose: () => void
  title?: string
  firstChildAsHeader?: boolean
  headerContent?: React.ReactNode
  className?: string
  variant?: 'standard' | 'blackout' | 'whiteout' | 'fullbleed'
  children?: React.ReactNode
  transitionTimeout?: number
  onExited?: () => void
}

export default function FloatingModal({
  show,
  onClose,
  title,
  firstChildAsHeader = true,
  headerContent,
  children,
  className,
  variant = 'standard',
  transitionTimeout = 200,
  onExited,
}: FloatingModalProps) {
  const { x, y, strategy, refs, context } = useFloating({
    open: show,
    onOpenChange: open => {
      if (!open) onClose()
    },
  })

  const dismiss = useDismiss(context, {
    outsidePress: (event: any): boolean => !event.target.closest('#js-calendar-target'),
  })
  const role = useRole(context)
  const { getFloatingProps } = useInteractions([dismiss, role])

  const handleCSSTransitionExited = () => {
    if (onExited) onExited()
  }

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onClose()
  }

  const modalClasses = classNames(
    'modal qa-modal',
    {
      'modal--full-bleed': variant === 'fullbleed',
    },
    className
  )

  const floatingOverlayClasses = classNames('modal-overlay', {
    'modal-overlay--whiteout': variant === 'whiteout',
    'modal-overlay--blackout': variant === 'blackout',
  })

  let header = null
  let bodyChildren = null
  if (firstChildAsHeader) {
    // Use headerContent or title if provided, otherwise, treat the first child as the header
    const childrenArray = React.Children.toArray(children)
    header = headerContent || (title && <h2 className="modal__title">{title}</h2>) || childrenArray[0]
    bodyChildren = headerContent || title ? children : childrenArray.slice(1)
  } else {
    // Just use children as the body
    bodyChildren = children
  }

  const closeIcon = <SVGIcon xlinkHref="#streamline-delete-1" title="Dismiss" alt="Dismiss" className="icon" />
  const headerSectionContent = header ? (
    <div className="modal__header p-x">
      {header}
      <Button onClick={handleDismiss} className="button-reset btn--icon qa-modal-close">
        {closeIcon}
      </Button>
    </div>
  ) : (
    <Button onClick={handleDismiss} className="button-reset modal__close-standalone p qa-modal-close">
      {closeIcon}
    </Button>
  )

  return (
    <FloatingPortal id="floating-portal-target">
      <CSSTransition
        in={show}
        timeout={transitionTimeout}
        classNames="modal-in-out"
        unmountOnExit
        onExited={handleCSSTransitionExited}
      >
        <div>
          <div className="modal-container">
            <FloatingOverlay lockScroll className={floatingOverlayClasses} onClick={onClose} />
            <FloatingFocusManager context={context} modal>
              <div
                ref={refs.setFloating}
                {...getFloatingProps({
                  className: modalClasses,
                  role: 'dialog',
                  'aria-labelledby': title ? 'modal-title' : undefined,
                  'aria-modal': true,
                })}
              >
                <div className="modal__content">
                  {headerSectionContent}
                  <div className="modal__scrollable p">{bodyChildren}</div>
                </div>
              </div>
            </FloatingFocusManager>
          </div>
        </div>
      </CSSTransition>
    </FloatingPortal>
  )
}
